/**
 * Product icon mapping utility
 * 
 * This file provides a unified interface for mapping product names/formats to their icon files,
 * whether they are print products or digital products.
 */

import { getPrintsizeIconFilename } from './printsizesMapping';
import { getDigitalProductIconFilename } from './digitalProductsMapping';

import { formatAandeelDisplay } from './utils/aandeelDisplay';

/**
 * Get the print size icon filename based on the Aandeel value
 * @param {string} aandeel - The Aandeel value (e.g., '1/1', '1/12', or decimal like 0.125)
 * @returns {string} The correct icon filename
 */
const getAandeelIconFilename = (aandeel) => {
  if (!aandeel) return 'print-placeholder.svg';
  
  // Normalise to fraction for lookup (e.g. 0.125 → '1/8')
  const cleanAandeel = formatAandeelDisplay(aandeel).trim();
  
  // Map common aandeel values to icon filenames
  const aandeelMapping = {
    '1/1': 'print-size101.svg',
    '1/2': 'print-size102.svg',
    '1/4': 'print-size104.svg',
    '1/6': 'print-size106.svg',
    '1/8': 'print-size108.svg',
    '1/12': 'print-size112.svg',
    '1/16': 'print-size116.svg',
    '1/20': 'print-size120.svg',
    '1/24': 'print-size124.svg',
    '1/32': 'print-size132.svg',
    '1/48': 'print-size148.svg',
    '1/64': 'print-size164.svg',
    '1/120': 'print-size120.svg',
    '1/166': 'print-size166.svg',
    '1/205': 'print-size205.svg',
    '1/308': 'print-size308.svg',
    '1/316': 'print-size316.svg',
    '1/320': 'print-size320.svg',
    '1/380': 'print-size380.svg',
    '2': 'print-size200.svg',
  };
  
  // Check if we have a direct mapping
  if (aandeelMapping[cleanAandeel]) {
    return aandeelMapping[cleanAandeel];
  }
  
  // If no direct mapping, try to parse the fraction and generate the filename
  const fractionMatch = cleanAandeel.match(/^(\d+)\/(\d+)$/);
  if (fractionMatch) {
    const numerator = parseInt(fractionMatch[1]);
    const denominator = parseInt(fractionMatch[2]);
    
    // Generate filename based on the fraction
    // For example: 1/12 -> print-size112.svg
    const sizeCode = `${numerator}${denominator}`;
    return `print-size${sizeCode}.svg`;
  }
  
  // Fallback to placeholder
  return 'print-placeholder.svg';
};

/**
 * Get the appropriate product icon filename based on the medium type and format
 * 
 * @param {Object} product - The product object
 * @param {string} product.Medium - The medium (e.g., 'Print', 'Online')
 * @param {string} product.Mediumtype - The medium type
 * @param {string} product.Formaat - The format (for print products)
 * @param {string} product.Aandeel - The aandeel value (for print products)
 * @param {string} product.Product - The product name (for digital products)
 * @returns {Object} Object containing the icon path and information
 */
export const getProductIcon = (product) => {
  if (!product) return { 
    iconPath: null,
    iconType: null,
    isPrint: false,
    isDigital: false
  };

  const isPrint = product.Medium === 'Print';
  const isDigital =
    product.Mediumtype === 'Online' ||
    product.Medium === 'Online' ||
    product.Mediumtype === 'Display' ||
    product.Medium === 'Display' ||
    product.Mediumtype === 'Video' ||
    product.Medium === 'Video' ||
    product.Mediumtype === 'Social';
  
  let iconFilename = null;
  let iconType = null;
  let iconPath = null;
  
  if (isPrint) {
    // For print products, use Formaat first, then fall back to Aandeel
    if (product.Formaat) {
      iconFilename = getPrintsizeIconFilename(product.Formaat);
      if (iconFilename) {
        iconType = 'print';
        iconPath = `/assets/printsizes/${iconFilename}`;
      }
    } else if (product.Aandeel) {
      // Fall back to Aandeel-based mapping
      iconFilename = getAandeelIconFilename(product.Aandeel);
      if (iconFilename) {
        iconType = 'print';
        iconPath = `/assets/printsizes/${iconFilename}`;
      }
    }
  } else if (isDigital) {
    // For digital products, try the Product field first, then fall back to Formaat
    // This handles cases where product details might be stored in different fields
    const productName = product.Product || product.Formaat;
    if (productName) {
      iconFilename = getDigitalProductIconFilename(productName);
      if (iconFilename) {
        iconType = 'digital';
        iconPath = `/assets/digitalproducts/${iconFilename}`;
      }
    }
  }
  
  return {
    iconPath,
    iconType,
    isPrint,
    isDigital,
    // Include placeholders for fallbacks
    placeholderPath: isPrint 
      ? '/assets/printsizes/print-placeholder.svg'
      : isDigital 
        ? '/assets/digitalproducts/digital-default.png' // Using default digital placeholder
        : null
  };
};

/**
 * Get the image element for a product icon with appropriate fallback
 * 
 * @param {Object} product - The product object
 * @returns {JSX.Element} JSX for image element with error handling
 */
export const getProductIconJSX = (product) => {
  const { iconPath, isPrint, isDigital, placeholderPath } = getProductIcon(product);
  
  if (!iconPath && !placeholderPath) {
    return null;
  }
  
  return {
    src: iconPath || placeholderPath,
    alt: isPrint 
      ? `${product.Formaat || 'Unknown'} size` 
      : `${product.Product || product.Formaat || 'Digital'} product`,
    onError: (e) => {
      e.target.onerror = null;
      e.target.src = placeholderPath;
    }
  };
};

export default {
  getProductIcon,
  getProductIconJSX
}; 